import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { CalendarClock, Pause, Play, Trash2 } from 'lucide-react';
import { RECURRING_FREQUENCY_LABELS, type RecurringFrequency } from '@fint/validation';
import { api } from '@/lib/api';
import { useActiveOrg } from '@/stores/auth';
import { PageHeader } from '@/app/DashboardLayout';
import { Button } from '@/features/common/ui/Button';
import { Card, CardContent, CardHeader, CardTitle } from '@/features/common/ui/Card';
import { EmptyState, ErrorState } from '@/features/common/ui/States';

interface Profile {
  id: string;
  name: string;
  customerName: string | null;
  frequency: RecurringFrequency;
  intervalCount: number;
  startDate: string;
  endDate: string | null;
  netDays: number;
  leadDays: number;
  nextRunDate: string | null;
  lastRunDate: string | null;
  status: 'active' | 'paused' | 'expired';
  /** Why it stopped itself. Null when a person paused it - nothing to explain. */
  pausedReason: string | null;
  pausedAt: string | null;
  raised: number;
}

/**
 * The subscriptions in this book.
 *
 * Deliberately a plain list and not a calendar: the question a person has is
 * "what is coming, and has anything stopped?", which is four columns and a
 * status. A schedule that has paused itself - a filed period is the usual
 * reason - says so here rather than in a log nobody reads.
 */
export function RecurringPage() {
  const queryClient = useQueryClient();
  const org = useActiveOrg();
  const list = useQuery({
    queryKey: ['recurring', org?.id],
    queryFn: () => api.get<{ items: Profile[] }>('/recurring?status=all'),
    enabled: !!org?.id,
  });
  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['recurring'] });
    void queryClient.invalidateQueries({ queryKey: ['invoices'] });
    void queryClient.invalidateQueries({ queryKey: ['inbox'] });
  };

  const setStatus = useMutation({
    mutationFn: (input: { id: string; status: 'active' | 'paused' }) =>
      api.patch(`/recurring/${input.id}`, { status: input.status }),
    onSuccess: refresh,
  });
  const runNow = useMutation({
    mutationFn: (id: string) => api.post(`/recurring/${id}/run`, {}),
    onSuccess: refresh,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/recurring/${id}`),
    onSuccess: refresh,
  });

  const items = list.data?.items ?? [];

  return (
    <>
      <PageHeader
        title="Subscriptions"
        subtitle="Invoices that repeat. Each one raises a draft; you issue it."
      />
      <div className="px-4 sm:px-5">
        {list.isError ? (
          <Card>
            <ErrorState error={list.error} onRetry={() => list.refetch()} />
          </Card>
        ) : items.length === 0 && !list.isLoading ? (
          <Card>
            <EmptyState
              icon={CalendarClock}
              title="Nothing repeats yet"
              description={
                'Open an invoice that happens again - a hosting re-charge, a retainer, a domain renewal - ' +
                'and choose Repeat. Fint raises a draft each time it falls due.'
              }
            />
          </Card>
        ) : (
          <div className="space-y-3">
            {items.map((profile) => (
              <Card key={profile.id} data-testid="recurring-card">
                <CardHeader className="pb-2">
                  <div className="flex flex-wrap items-start justify-between gap-2">
                    <div className="min-w-0">
                      <CardTitle className="truncate text-base" data-testid="recurring-name">
                        {profile.name}
                      </CardTitle>
                      <p className="text-sm text-muted-foreground">
                        {RECURRING_FREQUENCY_LABELS[profile.frequency]}
                        {profile.intervalCount > 1 ? ` (every ${profile.intervalCount})` : ''}
                        {profile.customerName ? ` · ${profile.customerName}` : ''}
                        {profile.netDays ? ` · payable in ${profile.netDays} days` : ''}
                      </p>
                    </div>
                    <StatusChip status={profile.status} />
                  </div>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <dl className="grid grid-cols-2 gap-x-4 gap-y-1 sm:grid-cols-4">
                    <Fact label="Next" value={profile.nextRunDate ? readable(profile.nextRunDate) : '—'} />
                    <Fact label="Last" value={profile.lastRunDate ? readable(profile.lastRunDate) : 'never'} />
                    <Fact label="Raised" value={`${profile.raised}`} />
                    <Fact label="Until" value={profile.endDate ? readable(profile.endDate) : 'no end'} />
                  </dl>
                  <div className="flex flex-wrap items-center gap-2">
                    {profile.status === 'active' ? (
                      <>
                        <Button
                          size="sm"
                          variant="outline"
                          loading={runNow.isPending && runNow.variables === profile.id}
                          onClick={() => runNow.mutate(profile.id)}
                          data-testid="recurring-run"
                        >
                          Raise what is due
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => setStatus.mutate({ id: profile.id, status: 'paused' })}
                          data-testid="recurring-pause"
                        >
                          <Pause className="size-4" aria-hidden />
                          Pause
                        </Button>
                      </>
                    ) : profile.status === 'paused' ? (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setStatus.mutate({ id: profile.id, status: 'active' })}
                        data-testid="recurring-resume"
                      >
                        <Play className="size-4" aria-hidden />
                        Resume
                      </Button>
                    ) : null}
                    <Button
                      size="sm"
                      variant="ghost"
                      className="text-destructive"
                      loading={remove.isPending && remove.variables === profile.id}
                      onClick={() => remove.mutate(profile.id)}
                      data-testid="recurring-delete"
                    >
                      <Trash2 className="size-4" aria-hidden />
                      Delete
                    </Button>
                    <Button asChild size="sm" variant="link" className="ml-auto px-1">
                      <Link to={`/invoices?status=draft`}>See the drafts</Link>
                    </Button>
                  </div>
                  {/* A schedule that stopped ITSELF says why, here, where the
                      person wondering about a missing invoice is looking. The
                      reason is in the audit trail too, but nobody opens that
                      to find out why nothing happened - and a stopped
                      subscription that looks like a paused one is how a month
                      goes unbilled. */}
                  {profile.status === 'paused' && profile.pausedReason ? (
                    <div
                      className="rounded-md border border-amber-300/60 bg-amber-50 p-2.5 text-xs dark:border-amber-500/30 dark:bg-amber-500/10"
                      data-testid="recurring-paused-reason"
                    >
                      <p className="font-medium text-amber-900 dark:text-amber-200">
                        Fint stopped this{profile.pausedAt ? ` on ${readable(profile.pausedAt.slice(0, 10))}` : ''}:
                      </p>
                      <p className="mt-0.5 text-amber-900/80 dark:text-amber-200/80">{profile.pausedReason}</p>
                      <p className="mt-1 text-amber-900/80 dark:text-amber-200/80">
                        Nothing has been raised since. Deal with it, then Resume - which starts from the
                        next date that has not passed.
                      </p>
                    </div>
                  ) : profile.status === 'paused' ? (
                    <p className="text-xs text-muted-foreground" data-testid="recurring-paused-note">
                      Paused. Nothing is raised while it is; resuming starts from the next date that has
                      not passed, so the months it slept through are not billed.
                    </p>
                  ) : null}
                </CardContent>
              </Card>
            ))}
          </div>
        )}
      </div>
    </>
  );
}

function Fact({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs text-muted-foreground">{label}</dt>
      <dd className="font-medium">{value}</dd>
    </div>
  );
}

function StatusChip({ status }: { status: Profile['status'] }) {
  const tone =
    status === 'active'
      ? 'bg-emerald-500/10 text-emerald-700 dark:text-emerald-300'
      : status === 'paused'
        ? 'bg-amber-500/10 text-amber-700 dark:text-amber-300'
        : 'bg-muted text-muted-foreground';
  return (
    <span className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${tone}`} data-testid="recurring-status">
      {status === 'active' ? 'Active' : status === 'paused' ? 'Paused' : 'Finished'}
    </span>
  );
}

function readable(iso: string): string {
  const [y, m, d] = iso.split('-');
  return `${d}-${m}-${y}`;
}
